import { useState } from 'react';
import { Search, Settings, Move, Edit2, Trash2, Plus, Upload } from 'lucide-react';

const categories = {
  'AI工具': {
    emoji: '🤖',
    links: [
      { name: 'ChatGPT', url: 'https://chat.openai.com', description: 'AI对话助手' },
      { name: 'Claude', url: 'https://claude.ai', description: '智能AI助手' },
      { name: 'Midjourney', url: 'https://midjourney.com', description: 'AI图像生成' }
    ]
  },
  '开发工具': {
    emoji: '💻',
    links: [
      { name: 'GitHub', url: 'https://github.com', description: '代码托管平台' },
      { name: 'Stack Overflow', url: 'https://stackoverflow.com', description: '开发者问答社区' },
      { name: 'VSCode', url: 'https://code.visualstudio.com', description: '代码编辑器' }
    ]
  },
  '设计资源': {
    emoji: '🎨',
    links: [
      { name: 'Figma', url: 'https://figma.com', description: '在线设计工具' },
      { name: 'Dribbble', url: 'https://dribbble.com', description: '设计灵感社区' },
      { name: 'Unsplash', url: 'https://unsplash.com', description: '免费图片资源' }
    ]
  }
};

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categoriesState, setCategoriesState] = useState(categories);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', emoji: '' });
  const [bulkLinks, setBulkLinks] = useState('');

  const filteredCategories = Object.entries(categoriesState).filter(([name, category]) => {
    if (activeCategory && activeCategory !== name) return false;
    
    if (searchTerm) {
      return category.links.some(link => 
        link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return true;
  });

  const handleAddCategory = () => {
    if (newCategory.name && newCategory.emoji) {
      setCategoriesState(prev => ({
        ...prev,
        [newCategory.name]: {
          emoji: newCategory.emoji,
          links: []
        }
      }));
      setNewCategory({ name: '', emoji: '' });
      setShowAddCategory(false);
    }
  };

  const handleDeleteCategory = (categoryName) => {
    if (window.confirm(`确定要删除 "${categoryName}" 分类吗？`)) {
      setCategoriesState(prev => {
        const newState = { ...prev };
        delete newState[categoryName];
        return newState;
      });
    }
  };

  const handleEditLink = (categoryName, linkIndex, field, value) => {
    setCategoriesState(prev => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        links: prev[categoryName].links.map((link, idx) =>
          idx === linkIndex ? { ...link, [field]: value } : link
        )
      }
    }));
  };

  const handleDeleteLink = (categoryName, linkIndex) => {
    if (window.confirm('确定要删除这个网站吗？')) {
      setCategoriesState(prev => ({
        ...prev,
        [categoryName]: {
          ...prev[categoryName],
          links: prev[categoryName].links.filter((_, idx) => idx !== linkIndex)
        }
      }));
    }
  };

  const handleBulkAdd = (categoryName) => {
    const newLinks = bulkLinks.split('\n').map(line => {
      const [name, url, description] = line.split(',').map(s => s.trim());
      return { name, url, description };
    }).filter(link => link.name && link.url);

    if (newLinks.length > 0) {
      setCategoriesState(prev => ({
        ...prev,
        [categoryName]: {
          ...prev[categoryName],
          links: [...prev[categoryName].links, ...newLinks]
        }
      }));
      setBulkLinks('');
      setShowBulkAdd(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">网址导航</h1>
          <p className="text-lg text-gray-600">收集实用网站，提高工作效率</p>
        </header>

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="搜索网站..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              {isEditMode && (
                <>
                  <button
                    onClick={() => setShowAddCategory(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Plus size={20} />
                    添加分类
                  </button>
                  <button
                    onClick={() => setShowBulkAdd(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Upload size={20} />
                    批量添加
                  </button>
                </>
              )}
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Settings size={20} />
                {isEditMode ? '退出编辑' : '编辑模式'}
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full transition-colors ${
              !activeCategory ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            全部
          </button>
          {Object.entries(categoriesState).map(([name, { emoji }]) => (
            <button
              key={name}
              onClick={() => setActiveCategory(name === activeCategory ? null : name)}
              className={`px-4 py-2 rounded-full transition-colors ${
                activeCategory === name ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {emoji} {name}
            </button>
          ))}
        </div>

        {/* Links Grid */}
        <div className="grid gap-8">
          {filteredCategories.map(([categoryName, category]) => (
            <div key={categoryName} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold flex items-center gap-2">
                  {category.emoji} {categoryName}
                </h2>
                {isEditMode && (
                  <button
                    onClick={() => handleDeleteCategory(categoryName)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.links.map((link, index) => (
                  <div
                    key={index}
                    className="group relative flex flex-col gap-2 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    {isEditMode ? (
                      <>
                        <div className="flex items-center gap-3">
                          <img
                            src={`${new URL(link.url).origin}/favicon.ico`}
                            alt=""
                            className="w-6 h-6 rounded"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /%3E%3C/svg%3E';
                            }}
                          />
                          <input
                            type="text"
                            value={link.name}
                            onChange={(e) => handleEditLink(categoryName, index, 'name', e.target.value)}
                            className="flex-1 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                          />
                        </div>
                        <input
                          type="text"
                          value={link.url}
                          onChange={(e) => handleEditLink(categoryName, index, 'url', e.target.value)}
                          className="text-sm text-gray-600 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="text"
                          value={link.description}
                          onChange={(e) => handleEditLink(categoryName, index, 'description', e.target.value)}
                          className="text-sm text-gray-600 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => handleDeleteLink(categoryName, index)}
                          className="absolute right-2 top-2 text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    ) : (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col gap-2"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={`${new URL(link.url).origin}/favicon.ico`}
                            alt=""
                            className="w-6 h-6 rounded"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" /%3E%3C/svg%3E';
                            }}
                          />
                          <span className="font-medium">{link.name}</span>
                        </div>
                        <p className="text-sm text-gray-600">{link.description}</p>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Add Category Modal */}
        {showAddCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h3 className="text-xl font-semibold mb-4">添加新分类</h3>
              <input
                type="text"
                placeholder="分类名称"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                className="w-full mb-4 p-2 border rounded"
              />
              <input
                type="text"
                placeholder="分类图标 (emoji)"
                value={newCategory.emoji}
                onChange={(e) => setNewCategory(prev => ({ ...prev, emoji: e.target.value }))}
                className="w-full mb-4 p-2 border rounded"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAddCategory(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  取消
                </button>
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Add Modal */}
        {showBulkAdd && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-[600px]">
              <h3 className="text-xl font-semibold mb-4">批量添加网站</h3>
              <p className="text-sm text-gray-600 mb-4">
                请按照以下格式输入网站信息，每行一个：<br />
                网站名称,网址,描述
              </p>
              <select
                className="w-full mb-4 p-2 border rounded"
                onChange={(e) => setActiveCategory(e.target.value)}
              >
                <option value="">选择分类</option>
                {Object.keys(categoriesState).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <textarea
                value={bulkLinks}
                onChange={(e) => setBulkLinks(e.target.value)}
                placeholder="示例：
GitHub,https://github.com,代码托管平台
Stack Overflow,https://stackoverflow.com,开发者问答社区"
                className="w-full h-40 mb-4 p-2 border rounded"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowBulkAdd(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  取消
                </button>
                <button
                  onClick={() => handleBulkAdd(activeCategory)}
                  disabled={!activeCategory}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;